import React from 'react';
import { Input, Select, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  IColumnMapping,
  IImportPlan,
  IPropertyColumn,
  TImportTarget,
} from '../../../../utils/asset-type-import';
import { IMapStepProps } from './i-import-wizard';

const TARGET_OPTIONS: { value: TImportTarget; label: string }[] = [
  { value: 'code', label: 'Code' },
  { value: 'description', label: 'Description' },
  { value: 'property', label: 'Property' },
  { value: 'ignore', label: 'Ignore' },
];

const headerCell = (): { style: React.CSSProperties } => ({
  style: {
    padding: '17px 10px',
    background: '#fafafa',
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(0, 0, 0, 0.85)',
  },
});

const bodyCell = (): { style: React.CSSProperties } => ({
  style: { padding: '8px 10px', fontSize: 13, color: '#535559' },
});

const buildPlan = (columns: IColumnMapping[], plan: IImportPlan): IImportPlan => {
  const codeColumn = columns.findIndex((column) => column.target === 'code');
  const descriptionColumn = columns.findIndex((column) => column.target === 'description');
  const propertyColumns: IPropertyColumn[] = columns
    .filter((column) => column.target === 'property')
    .map((column) => ({ columnIndex: column.columnIndex, name: column.propertyName }));

  const names = new Set(propertyColumns.map((column) => column.name));

  return {
    columns,
    codeColumn,
    descriptionColumn,
    propertyColumns,
    confident: codeColumn >= 0 && descriptionColumn >= 0,
    warnings: plan.warnings,
    propertyRenames: plan.propertyRenames.filter((rename) => names.has(rename.to)),
  };
};

const collectBlockers = (plan: IImportPlan): string[] => {
  const blockers: string[] = [];

  if (plan.codeColumn < 0) blockers.push('Map a column to Code');
  if (plan.descriptionColumn < 0) blockers.push('Map a column to Description');

  const names = plan.propertyColumns.map((property) => property.name.trim());
  if (names.some((name) => name.length === 0)) blockers.push('Every property needs a name');

  const unique = new Set(names.filter((name) => name.length > 0).map((n) => n.toLowerCase()));
  if (unique.size !== names.filter((name) => name.length > 0).length) {
    blockers.push('Property names must be unique');
  }

  return blockers;
};

const MapColumnsStep = ({ plan, samples, onChange }: IMapStepProps): JSX.Element => {
  const blockers = collectBlockers(plan);

  const setTarget = (columnIndex: number, target: TImportTarget): void => {
    const exclusive = target === 'code' || target === 'description';

    const columns = plan.columns.map((column) => {
      if (column.columnIndex === columnIndex) {
        const keepsName = target !== 'property' || column.propertyName.trim().length > 0;
        return {
          ...column,
          target,
          propertyName: keepsName ? column.propertyName : column.header,
        };
      }

      if (exclusive && column.target === target) {
        return {
          ...column,
          target: 'property' as TImportTarget,
          propertyName: column.propertyName.trim().length > 0 ? column.propertyName : column.header,
        };
      }

      return column;
    });

    onChange(buildPlan(columns, plan));
  };

  const setPropertyName = (columnIndex: number, name: string): void => {
    const columns = plan.columns.map((column) => (
      column.columnIndex === columnIndex ? { ...column, propertyName: name } : column
    ));

    onChange(buildPlan(columns, plan));
  };

  const columns: ColumnsType<IColumnMapping> = [
    {
      title: '#',
      key: 'index',
      width: 56,
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => column.columnIndex + 1,
    },
    {
      title: 'Header',
      key: 'header',
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => (
        <span style={{ fontSize: 13, color: '#535559' }}>{column.header}</span>
      ),
    },
    {
      title: 'Sample',
      key: 'sample',
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => {
        const sample = samples[column.columnIndex] ?? '';
        return (
          <span
            title={sample}
            style={{
              display: 'block',
              maxWidth: 220,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#8C909B',
            }}
          >
            {sample}
          </span>
        );
      },
    },
    {
      title: 'Maps to',
      key: 'target',
      width: 180,
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => (
        <Select<TImportTarget>
          size="small"
          style={{ width: 160 }}
          value={column.target}
          options={TARGET_OPTIONS}
          onChange={(target) => setTarget(column.columnIndex, target)}
        />
      ),
    },
    {
      title: 'Property name',
      key: 'propertyName',
      onHeaderCell: headerCell,
      onCell: bodyCell,
      render: (_value: unknown, column: IColumnMapping) => (
        <Input
          size="small"
          value={column.propertyName}
          disabled={column.target !== 'property'}
          onChange={(event) => setPropertyName(column.columnIndex, event.target.value)}
        />
      ),
    },
  ];

  return (
    <div>
      {plan.warnings.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {plan.warnings.map((warning) => (
            <div key={warning} style={{ fontSize: 12, color: '#8C909B' }}>{warning}</div>
          ))}
        </div>
      )}

      <div style={{ border: '1px solid #f1f2f5', borderRadius: 11, overflow: 'hidden' }}>
        <Table<IColumnMapping>
          size="small"
          columns={columns}
          dataSource={plan.columns}
          rowKey={(column) => String(column.columnIndex)}
          pagination={false}
        />
      </div>

      {blockers.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {blockers.map((blocker) => (
            <div key={blocker} style={{ fontSize: 12, color: '#E13333' }}>{blocker}</div>
          ))}
        </div>
      )}
    </div>
  );
};

MapColumnsStep.displayName = 'MapColumnsStep';

export default MapColumnsStep;
